// src/components/AudioRecorder.js
import React, { useRef, useState, useEffect } from 'react';

const MAX_SILENCE_SECONDS = 600; // 10 minutes

/**
 * Props:
 * - onStartRecording?: () => void
 * - onStopRecording: (blob: Blob, metadata: {
 *     timeIn: string | null,
 *     timeOut: string | null,
 *     durationMinutes: number | null,
 *     isManualTimeEntry: boolean,
 *     sessionId: string
 *   }) => void
 */
const AudioRecorder = ({ onStartRecording, onStopRecording }) => {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const intervalRef = useRef(null);

  const timeInRef = useRef(null);
  const timeOutRef = useRef(null);
  const elapsedRef = useRef(0);
  const sessionIdRef = useRef(null); // ✅ shared sessionId

  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const chunks = [];
      mediaRecorderRef.current = new MediaRecorder(stream);
      sessionIdRef.current = `session-${Date.now()}`; // ✅ generate once
      elapsedRef.current = 0;
      setElapsed(0);
      console.log("🆔 Generated sessionId:", sessionIdRef.current);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorderRef.current.onstart = () => {
        timeInRef.current = new Date();
        // 🔔 Notify parent that recording truly started
        onStartRecording?.();
      };

      mediaRecorderRef.current.onstop = () => {
        stopElapsedTimer();
        stopSilenceDetection();
        cleanupAudioContext();

        timeOutRef.current = new Date();

        const stream = streamRef.current;
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        if (!chunks.length) {
          console.warn("⚠️ No audio chunks recorded.");
          return;
        }

        const blob = new Blob(chunks, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioURL(url);

        const audio = new Audio(url);
        audio.onloadedmetadata = () => {
          console.log("📏 Recorded duration:", audio.duration);
        };

        const timeInISO = timeInRef.current?.toISOString() || null;
        const timeOutISO = timeOutRef.current?.toISOString() || null;
        const durationMinutes =
          timeInRef.current
            ? Math.round(elapsedRef.current / 60)
            : null;

        onStopRecording?.(blob, {
          timeIn: timeInISO,
          timeOut: timeOutISO,
          durationMinutes,
          isManualTimeEntry: false,
          sessionId: sessionIdRef.current, // ✅ pass along
        });
      };

      // Start recording (timeslice 1000ms)
      mediaRecorderRef.current.start(1000);
      setRecording(true);
      setPaused(false);

      // Elapsed timer and silence detection
      startElapsedTimer();
      setupSilenceDetection(stream);
    } catch (error) {
      console.error("🎙️ Mic access error:", error);
      // If mic fails, make sure UI reflects that recording isn't active
      setRecording(false);
    }
  };

  const stopRecording = () => {
    if (["recording", "paused"].includes(mediaRecorderRef.current?.state)) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setPaused(false);
    }
  };

  const togglePause = () => {
    const recorder = mediaRecorderRef.current;

    if (recorder?.state === "recording") {
      recorder.pause();
      stopElapsedTimer();
      stopSilenceDetection();
      cleanupAudioContext();
      setPaused(true);
      return;
    }

    if (recorder?.state === "paused" && streamRef.current) {
      recorder.resume();
      startElapsedTimer();
      setupSilenceDetection(streamRef.current);
      setPaused(false);
    }
  };

  const startElapsedTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
    }, 1000);
  };

  const stopElapsedTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  const stopSilenceDetection = () => {
    clearInterval(silenceTimerRef.current);
    silenceTimerRef.current = null;
  };

  const setupSilenceDetection = (stream) => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContextRef.current.createMediaStreamSource(stream);
    const analyser = audioContextRef.current.createAnalyser();
    analyser.fftSize = 2048;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.fftSize);
    source.connect(analyser);

    let silenceDuration = 0;

    silenceTimerRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(dataArray);

      const rms = Math.sqrt(
        dataArray.reduce((sum, val) => {
          const normalized = (val - 128) / 128;
          return sum + normalized * normalized;
        }, 0) / dataArray.length
      );

      if (rms < 0.02) {
        silenceDuration++;
        if (silenceDuration >= MAX_SILENCE_SECONDS) {
          console.warn("🛑 Auto-stopping due to 10 minutes of silence");
          stopRecording();
        }
      } else {
        silenceDuration = 0;
      }
    }, 1000);
  };

  const cleanupAudioContext = () => {
    try {
      analyserRef.current?.disconnect();
      analyserRef.current = null;
      audioContextRef.current?.close();
      audioContextRef.current = null;
    } catch (e) {
      const message =
        typeof e === "object" && e !== null && "message" in e
          ? e.message
          : JSON.stringify(e);
      console.warn("🎧 Failed to clean up AudioContext:", message);
    }
  };

  useEffect(() => {
    return () => {
      stopElapsedTimer();
      stopSilenceDetection();
      cleanupAudioContext();

      const stream = streamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return (
    <div className="p-4 border rounded shadow">
      <div className="audio-recorder-controls">
        <button
          onClick={startRecording}
          disabled={recording}
          className="bg-green-500 text-white px-3 py-1.5 rounded text-sm font-semibold"
        >
          Start Listening
        </button>
        <button
          onClick={stopRecording}
          disabled={!recording}
          className={`bg-red-500 text-white px-3 py-1.5 rounded text-sm font-semibold ${recording ? 'pulsing' : ''}`}
        >
          Stop Listening
        </button>
        <button
          onClick={togglePause}
          disabled={!recording}
          className="bg-yellow-500 text-white px-3 py-1.5 rounded text-sm font-semibold"
        >
          {paused ? 'Resume Listening' : 'Pause Listening'}
        </button>
        {recording && (
          <span className="text-gray-600 text-sm">
            {paused ? 'Paused. ' : ''}Elapsed: {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
          </span>
        )}
      </div>
    </div>
  );
};

export default AudioRecorder;
